/**
 * Immutable command pattern for Paper2Ink vector editor.
 * Guarantees lossless undo/redo, single-command drag interactions,
 * and zero base64 source snapshot duplication.
 */

import type { CIMDocument, InkObject } from './model.ts';
import { validateColor } from './model.ts';

export interface Command {
  readonly description: string;
  execute(doc: CIMDocument): CIMDocument;
  undo(doc: CIMDocument): CIMDocument;
}

function withInvalidatedDeclaration(doc: CIMDocument, newObjects: InkObject[]): CIMDocument {
  const isChanged = newObjects !== doc.objects;
  if (!isChanged && doc.review.user_declaration === null) {
    return doc;
  }
  return {
    ...doc,
    objects: newObjects,
    review: {
      ...doc.review,
      user_declaration: null,
    },
  };
}

export class MoveObjectsCommand implements Command {
  readonly description: string;
  private readonly targetIds: Set<string>;
  private readonly dx: number;
  private readonly dy: number;
  private prevTransforms: Map<string, [number, number, number, number, number, number]> = new Map();

  constructor(objectIds: readonly string[], dx: number, dy: number) {
    this.targetIds = new Set(objectIds);
    this.dx = dx;
    this.dy = dy;
    this.description = `Move ${objectIds.length} object(s) by (${dx}, ${dy})`;
  }

  getTargetIds(): string[] {
    return Array.from(this.targetIds);
  }

  getDx(): number {
    return this.dx;
  }

  getDy(): number {
    return this.dy;
  }

  execute(doc: CIMDocument): CIMDocument {
    this.prevTransforms.clear();
    const updated = doc.objects.map((obj) => {
      if (!this.targetIds.has(obj.id)) return obj;
      const [a, b, c, d, e, f] = obj.transform;
      this.prevTransforms.set(obj.id, [a, b, c, d, e, f]);
      return {
        ...obj,
        x: e + this.dx,
        y: f + this.dy,
        transform: [a, b, c, d, e + this.dx, f + this.dy] as [number, number, number, number, number, number],
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }

  undo(doc: CIMDocument): CIMDocument {
    const updated = doc.objects.map((obj) => {
      const prev = this.prevTransforms.get(obj.id);
      if (!prev) return obj;
      return {
        ...obj,
        x: prev[4],
        y: prev[5],
        transform: [...prev] as [number, number, number, number, number, number],
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }
}

export class RecolorObjectsCommand implements Command {
  readonly description: string;
  private readonly targetIds: Set<string>;
  private readonly newColor: string;
  private prevColors: Map<string, string> = new Map();

  constructor(objectIds: readonly string[], newColor: string) {
    this.targetIds = new Set(objectIds);
    this.newColor = validateColor(newColor);
    this.description = `Recolor ${objectIds.length} object(s) to ${this.newColor}`;
  }

  execute(doc: CIMDocument): CIMDocument {
    this.prevColors.clear();
    const updated = doc.objects.map((obj) => {
      if (!this.targetIds.has(obj.id)) return obj;
      this.prevColors.set(obj.id, obj.color);
      return {
        ...obj,
        color: this.newColor,
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }

  undo(doc: CIMDocument): CIMDocument {
    const updated = doc.objects.map((obj) => {
      const prev = this.prevColors.get(obj.id);
      if (!prev) return obj;
      return {
        ...obj,
        color: prev,
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }
}

export class ScaleObjectsCommand implements Command {
  readonly description: string;
  private readonly targetIds: Set<string>;
  private readonly factor: number;
  private prevTransforms: Map<string, [number, number, number, number, number, number]> = new Map();

  constructor(objectIds: readonly string[], factor: number) {
    if (factor <= 0 || !Number.isFinite(factor)) {
      throw new Error(`Scale factor must be positive finite number, got ${factor}`);
    }
    this.targetIds = new Set(objectIds);
    this.factor = factor;
    this.description = `Scale ${objectIds.length} object(s) by ${factor}`;
  }

  execute(doc: CIMDocument): CIMDocument {
    this.prevTransforms.clear();
    const updated = doc.objects.map((obj) => {
      if (!this.targetIds.has(obj.id)) return obj;
      const [a, b, c, d, e, f] = obj.transform;
      this.prevTransforms.set(obj.id, [a, b, c, d, e, f]);
      const prevScale = (obj as any).scale ?? 1;
      return {
        ...obj,
        scale: prevScale * this.factor,
        transform: [a * this.factor, b * this.factor, c * this.factor, d * this.factor, e, f] as [number, number, number, number, number, number],
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }

  undo(doc: CIMDocument): CIMDocument {
    const updated = doc.objects.map((obj) => {
      const prev = this.prevTransforms.get(obj.id);
      if (!prev) return obj;
      return {
        ...obj,
        scale: 1,
        transform: [...prev] as [number, number, number, number, number, number],
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }
}

export class DeleteObjectsCommand implements Command {
  readonly description: string;
  private readonly targetIds: Set<string>;
  private deletedItems: Array<{ index: number; object: InkObject }> = [];

  constructor(objectIds: readonly string[]) {
    this.targetIds = new Set(objectIds);
    this.description = `Delete ${objectIds.length} object(s)`;
  }

  execute(doc: CIMDocument): CIMDocument {
    this.deletedItems = [];
    const remaining: InkObject[] = [];
    doc.objects.forEach((obj, idx) => {
      if (this.targetIds.has(obj.id)) {
        this.deletedItems.push({ index: idx, object: obj });
      } else {
        remaining.push(obj);
      }
    });
    return withInvalidatedDeclaration(doc, remaining);
  }

  undo(doc: CIMDocument): CIMDocument {
    const restored = [...doc.objects];
    // Insert back at original indices
    for (const item of this.deletedItems) {
      restored.splice(item.index, 0, item.object);
    }
    return withInvalidatedDeclaration(doc, restored);
  }
}

export class AddObjectsCommand implements Command {
  readonly description: string;
  private readonly newObjects: readonly InkObject[];

  constructor(newObjects: readonly InkObject[]) {
    this.newObjects = newObjects;
    this.description = `Add ${newObjects.length} object(s)`;
  }

  getAddedObjects(): readonly InkObject[] {
    return this.newObjects;
  }

  execute(doc: CIMDocument): CIMDocument {
    const updated = [...doc.objects, ...this.newObjects];
    return withInvalidatedDeclaration(doc, updated);
  }

  undo(doc: CIMDocument): CIMDocument {
    const addedIds = new Set(this.newObjects.map((o) => o.id));
    const updated = doc.objects.filter((o) => !addedIds.has(o.id));
    return withInvalidatedDeclaration(doc, updated);
  }
}

export class RotateObjectsCommand implements Command {
  readonly description: string;
  private readonly targetIds: Set<string>;
  private readonly cos: number;
  private readonly sin: number;
  private readonly pivotX: number;
  private readonly pivotY: number;
  private prevTransforms: Map<string, [number, number, number, number, number, number]> = new Map();

  constructor(objectIds: readonly string[], angleDegrees: number, pivot: readonly [number, number]) {
    if (!Number.isFinite(angleDegrees) || !Number.isFinite(pivot[0]) || !Number.isFinite(pivot[1])) {
      throw new Error(`Rotation angle and pivot must be finite numbers, got ${angleDegrees}, [${pivot[0]}, ${pivot[1]}]`);
    }
    this.targetIds = new Set(objectIds);
    // Normalize to [0, 360) so negative and over-full-turn angles behave predictably.
    const normalized = ((angleDegrees % 360) + 360) % 360;
    const rad = (normalized * Math.PI) / 180;
    this.cos = Math.cos(rad);
    this.sin = Math.sin(rad);
    this.pivotX = pivot[0];
    this.pivotY = pivot[1];
    this.description = `Rotate ${objectIds.length} object(s) by ${normalized}deg around (${pivot[0]}, ${pivot[1]})`;
  }

  getTargetIds(): string[] {
    return Array.from(this.targetIds);
  }

  getAngleDegrees(): number {
    // Recover a representative angle in [0, 360) from cos/sin (atan2 sin, cos).
    const rad = Math.atan2(this.sin, this.cos);
    return (((rad * 180) / Math.PI) % 360 + 360) % 360;
  }

  execute(doc: CIMDocument): CIMDocument {
    this.prevTransforms.clear();
    const updated = doc.objects.map((obj) => {
      if (!this.targetIds.has(obj.id)) return obj;
      const [a, b, c, d, e, f] = obj.transform;
      this.prevTransforms.set(obj.id, [a, b, c, d, e, f]);
      const cos = this.cos;
      const sin = this.sin;
      // M' = R(theta, pivot) * M: left-multiply in world space so the object
      // rotates about the pivot regardless of its existing linear part.
      // R uses the SVG rotate(a) convention: [cos, sin, -sin, cos].
      const na = cos * a - sin * b;
      const nb = sin * a + cos * b;
      const nc = cos * c - sin * d;
      const nd = sin * c + cos * d;
      const tx = this.pivotX * (1 - cos) + this.pivotY * sin;
      const ty = this.pivotY * (1 - cos) - this.pivotX * sin;
      // Left-multiply in world space: M' = R * M, so the new translation is
      // cosθ·e − sinθ·f + tx, sinθ·e + cosθ·f + ty.
      const ne = cos * e - sin * f + tx;
      const nf = sin * e + cos * f + ty;
      return {
        ...obj,
        transform: [na, nb, nc, nd, ne, nf] as [number, number, number, number, number, number],
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }

  undo(doc: CIMDocument): CIMDocument {
    const updated = doc.objects.map((obj) => {
      const prev = this.prevTransforms.get(obj.id);
      if (!prev) return obj;
      return {
        ...obj,
        transform: [...prev] as [number, number, number, number, number, number],
      };
    });
    return withInvalidatedDeclaration(doc, updated);
  }
}

export class BatchCommand implements Command {
  readonly description: string;
  private readonly commands: readonly Command[];

  constructor(description: string, commands: readonly Command[]) {
    this.description = description;
    this.commands = commands;
  }

  execute(doc: CIMDocument): CIMDocument {
    let current = doc;
    for (const cmd of this.commands) {
      current = cmd.execute(current);
    }
    return current;
  }

  undo(doc: CIMDocument): CIMDocument {
    let current = doc;
    for (let i = this.commands.length - 1; i >= 0; i--) {
      current = this.commands[i].undo(current);
    }
    return current;
  }
}

export { CommandHistory } from './history.ts';
